import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BottomNav from "@/components/BottomNav";
import LoadingScreen from "@/components/LoadingScreen";
import { api } from "@/lib/api";
import ConfigInspector from "@/components/ConfigInspector";
import {
  Users, TrendingUp, Calendar, Globe, BookOpen, GraduationCap,
  Coins, Library as LibraryIcon, ShieldAlert, RefreshCw, DollarSign,
  EyeOff, Eye as EyeIcon, ExternalLink, Gamepad2,
} from "lucide-react";
import { toast } from "sonner";

function MetricCard({ label, value, sub, Icon, accent = "cobalt", testid }) {
  const accents = {
    cobalt: "border-[#0044FF]/30 bg-[#0044FF]/[0.04] text-[#8aa8ff]",
    mint: "border-[#00FF9D]/30 bg-[#00FF9D]/[0.04] text-[#00FF9D]",
    violet: "border-[#5D3FD3]/30 bg-[#5D3FD3]/[0.04] text-[#b9a8ff]",
    orange: "border-[#FF7B00]/30 bg-[#FF7B00]/[0.04] text-[#ffb067]",
  }[accent];
  return (
    <div className={`glass p-5 border ${accents.split(" ").slice(0, 1).join(" ")}`} data-testid={testid}>
      <div className="flex items-center gap-2 label-eyebrow">
        <Icon size={12} className={accents.split(" ").slice(-1).join(" ")} /> {label}
      </div>
      <div className="font-display text-3xl md:text-4xl tracking-tight mt-2">{value}</div>
      {sub ? <div className="text-xs text-white/45 mt-1">{sub}</div> : null}
    </div>
  );
}

function Bar({ label, count, max, accent = "#00FF9D" }) {
  const pct = max > 0 ? Math.max(4, Math.round((count / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="w-16 text-[11px] font-mono-oak text-white/55 truncate">{label}</div>
      <div className="flex-1 h-2 rounded-full bg-white/[0.04] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: accent }} />
      </div>
      <div className="w-10 text-right text-xs font-mono-oak text-white/70">{count}</div>
    </div>
  );
}

export default function Admin() {
  const [state, setState] = useState({ loading: true, authorized: null, data: null, err: null });
  const navigate = useNavigate();

  const load = async () => {
    setState((s) => ({ ...s, loading: true, err: null }));
    try {
      const { data: who } = await api.get("/admin/whoami");
      if (!who.is_admin) {
        setState({ loading: false, authorized: false, data: null, err: null });
        return;
      }
      const { data } = await api.get("/admin/metrics");
      setState({ loading: false, authorized: true, data, err: null });
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) return navigate("/");
      setState({ loading: false, authorized: code === 403 ? false : null, data: null, err: e?.response?.data?.detail || e.message });
    }
  };

  useEffect(() => { load(); }, []);

  if (state.loading) return <LoadingScreen />;

  if (state.authorized === false) {
    return (
      <div className="min-h-screen grid place-items-center px-6" data-testid="admin-not-authorized">
        <div className="glass p-10 max-w-md w-full text-center">
          <ShieldAlert className="mx-auto text-[#ffb067] mb-3" size={36} />
          <div className="font-display text-2xl tracking-tight">Admin access only</div>
          <div className="text-white/55 text-sm mt-2">This dashboard is restricted to Oak AI Campus administrators.</div>
          <button
            onClick={() => navigate("/")}
            className="mt-6 btn-primary rounded-lg px-4 py-2 text-sm font-display"
            data-testid="btn-admin-home"
          >
            Back to Hub
          </button>
        </div>
      </div>
    );
  }

  const d = state.data;
  const maxSignup = d?.signups_by_day?.reduce((m, r) => Math.max(m, r.count), 0) || 1;
  const maxLocale = d?.locales?.reduce((m, r) => Math.max(m, r.count), 0) || 1;

  return (
    <div className="min-h-screen">
      <Header credits={0} onOpenAd={() => {}} onOpenCheckout={() => {}} />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 space-y-10" data-testid="admin-dashboard">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow mb-2">Oak AI Campus · Internal</div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight">Admin Metrics</h1>
            <div className="text-white/45 text-xs mt-1 font-mono-oak">
              Generated {d?.generated_at ? new Date(d.generated_at).toLocaleString() : "…"}
            </div>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/10 bg-white/[0.04] text-white/75 hover:bg-white/10 transition"
            data-testid="btn-admin-refresh"
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        {/* Launch-day config inspector — mounted first so missing keys are
            the first thing an admin sees on /admin. */}
        <ConfigInspector />

        {/* USERS */}
        <section>
          <div className="label-eyebrow mb-3">Users</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-users-cards">
            <MetricCard label="Total users" value={d.users.total} Icon={Users} accent="cobalt" testid="metric-total" />
            <MetricCard label="DAU" value={d.users.dau} sub="active last 24h" Icon={TrendingUp} accent="mint" testid="metric-dau" />
            <MetricCard label="WAU" value={d.users.wau} sub="active last 7d" Icon={TrendingUp} accent="violet" testid="metric-wau" />
            <MetricCard label="MAU" value={d.users.mau} sub="active last 30d" Icon={Calendar} accent="orange" testid="metric-mau" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <MetricCard label="New signups · 7d" value={d.users.new_7d} Icon={Users} accent="mint" testid="metric-new-7d" />
            <MetricCard label="New signups · 30d" value={d.users.new_30d} Icon={Users} accent="violet" testid="metric-new-30d" />
          </div>
        </section>

        {/* ENGAGEMENT */}
        <section>
          <div className="label-eyebrow mb-3">Engagement</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="admin-engagement-cards">
            <MetricCard label="Paths generated" value={d.engagement.paths} Icon={BookOpen} accent="cobalt" />
            <MetricCard label="Modules completed" value={d.engagement.modules_completed} sub="3-part whitepapers" Icon={GraduationCap} accent="mint" />
            <MetricCard label="Exams submitted" value={d.engagement.exams_submitted} Icon={GraduationCap} accent="violet" />
            <MetricCard label="Library saved" value={d.engagement.library_saved} sub="PDFs" Icon={LibraryIcon} accent="orange" />
            <MetricCard label="Credits spent · 7d" value={d.engagement.credits_spent_7d} Icon={Coins} accent="mint" />
          </div>
        </section>

        {/* REVENUE */}
        <section>
          <div className="flex items-end justify-between mb-3">
            <div className="label-eyebrow">Revenue · last 7 days</div>
            <button
              type="button"
              onClick={() => navigate("/admin/revenue")}
              className="text-[11px] uppercase tracking-wider text-[#9bffcc] hover:text-[#00FF9D] inline-flex items-center gap-1.5 transition"
              data-testid="link-admin-revenue-dashboard"
            >
              Open revenue dashboard <ExternalLink size={11} />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="admin-revenue-cards">
            <MetricCard
              label="INR revenue · 7d"
              value={`₹${(d.revenue?.inr_7d ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
              sub={`${d.revenue?.inr_orders_7d ?? 0} order${(d.revenue?.inr_orders_7d ?? 0) === 1 ? "" : "s"} · Razorpay`}
              Icon={DollarSign}
              accent="mint"
              testid="metric-revenue-7d"
            />
            <MetricCard
              label="Avg order value"
              value={`₹${d.revenue?.inr_orders_7d > 0 ? Math.round(d.revenue.inr_7d / d.revenue.inr_orders_7d).toLocaleString("en-IN") : "0"}`}
              sub="INR per order"
              Icon={DollarSign}
              accent="violet"
              testid="metric-aov"
            />
            <MetricCard
              label="Credits sold · 7d"
              value={(d.revenue?.inr_orders_7d ?? 0) * 500}
              sub="≈ avg per order"
              Icon={Coins}
              accent="orange"
              testid="metric-credits-sold"
            />
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LOCALES */}
          <section className="glass p-6" data-testid="admin-locales">
            <div className="flex items-center gap-2 label-eyebrow mb-4">
              <Globe size={12} className="text-[#8aa8ff]" /> Locale split
            </div>
            {d.locales.length === 0 ? (
              <div className="text-white/40 text-sm">No data yet.</div>
            ) : (
              <div className="space-y-2.5">
                {d.locales.map((l) => (
                  <Bar key={l.locale} label={l.locale} count={l.count} max={maxLocale} accent="#0044FF" />
                ))}
              </div>
            )}
          </section>

          {/* SIGNUPS 14D */}
          <section className="glass p-6" data-testid="admin-signups">
            <div className="flex items-center gap-2 label-eyebrow mb-4">
              <Calendar size={12} className="text-[#00FF9D]" /> Signups · last 14 days
            </div>
            {d.signups_by_day.length === 0 ? (
              <div className="text-white/40 text-sm">No signups in the last 14 days.</div>
            ) : (
              <div className="flex items-end gap-1.5 h-40">
                {d.signups_by_day.map((row) => {
                  const h = Math.max(6, Math.round((row.count / maxSignup) * 100));
                  return (
                    <div key={row.day} className="flex-1 flex flex-col items-center gap-1.5" title={`${row.day}: ${row.count}`}>
                      <div className="w-full rounded-t bg-gradient-to-t from-[#00FF9D]/80 to-[#00FF9D]/30" style={{ height: `${h}%` }} />
                      <div className="text-[9px] font-mono-oak text-white/35">{row.day.slice(5)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        {/* Iter67 — Mini-game analytics. Pass rate × replay rate per
            mechanic tells us which generators engage learners. Hidden
            entirely when no games have been played in the last 14d. */}
        {d.games?.summary?.played_14d > 0 && (
          <GamesAnalyticsSection games={d.games} />
        )}

        {/* TOP PATHS */}
        <section className="glass p-6" data-testid="admin-top-paths">
          <div className="flex items-center gap-2 label-eyebrow mb-4">
            <BookOpen size={12} className="text-[#ffb067]" /> Top saved paths
          </div>
          {d.top_paths.length === 0 ? (
            <div className="text-white/40 text-sm">No saves yet.</div>
          ) : (
            <div className="divide-y divide-white/5">
              {d.top_paths.map((p, i) => (
                <div key={p.path_id} className="flex items-center gap-4 py-3">
                  <div className="font-display text-lg text-white/40 w-6 text-right">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-sm truncate">{p.goal || "(no title)"}</div>
                    <div className="text-[11px] font-mono-oak text-white/35 truncate">{p.path_id}</div>
                  </div>
                  <div className="text-xs font-mono-oak text-[#ffb067]">{p.saves} save{p.saves === 1 ? "" : "s"}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        {state.err ? (
          <div className="glass p-4 border-rose-400/30 text-rose-200 text-sm" data-testid="admin-error">
            {state.err}
          </div>
        ) : null}

        <ModerationSection />
      </main>
      <Footer />
      <BottomNav />
    </div>
  );
}

/**
 * Public Notes moderation panel — admin-only. Lists newest study guides,
 * shows a 400-char preview, lets admins hide / unhide. Hidden notes are
 * filtered out of the public reader, OG endpoints, and the SEO sitemap
 * automatically (server enforces).
 */
function ModerationSection() {
  const [state, setState] = useState({ loading: true, notes: [], err: null });
  const [includeHidden, setIncludeHidden] = useState(true);

  const refresh = async () => {
    setState((s) => ({ ...s, loading: true, err: null }));
    try {
      const { data } = await api.get(`/admin/public-notes?limit=50&include_hidden=${includeHidden}`);
      setState({ loading: false, notes: data?.notes || [], err: null });
    } catch (e) {
      setState({ loading: false, notes: [], err: e?.response?.data?.detail || "Could not load notes" });
    }
  };

  useEffect(() => { refresh(); }, [includeHidden]); // eslint-disable-line react-hooks/exhaustive-deps

  const hide = async (slug) => {
    const reason = window.prompt("Reason for hiding (optional, internal-only):", "");
    if (reason === null) return; // user cancelled
    try {
      await api.post(`/admin/public-notes/${slug}/hide`, { reason });
      toast.success("Note hidden from public view");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hide failed");
    }
  };

  const unhide = async (slug) => {
    try {
      await api.post(`/admin/public-notes/${slug}/unhide`);
      toast.success("Note restored");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unhide failed");
    }
  };

  return (
    <section className="glass p-6" data-testid="admin-moderation">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2 label-eyebrow">
          <ShieldAlert size={12} className="text-[#ffb067]" /> Public notes moderation
        </div>
        <div className="flex items-center gap-3">
          <label className="text-[11px] text-white/55 inline-flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={includeHidden}
              onChange={(e) => setIncludeHidden(e.target.checked)}
              className="accent-[#0044FF]"
              data-testid="admin-moderation-include-hidden"
            />
            Show hidden
          </label>
          <button
            onClick={refresh}
            className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-white/10 hover:border-white/20 text-white/65 hover:text-white transition"
            data-testid="admin-moderation-refresh"
          >
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>

      {state.err && <div className="text-rose-200 text-sm" data-testid="admin-moderation-error">{state.err}</div>}
      {state.loading ? (
        <div className="text-white/40 text-sm">Loading…</div>
      ) : state.notes.length === 0 ? (
        <div className="text-white/40 text-sm" data-testid="admin-moderation-empty">No public notes yet.</div>
      ) : (
        <div className="divide-y divide-white/5" data-testid="admin-moderation-list">
          {state.notes.map((n) => (
            <div key={n.slug} className="py-4 flex items-start gap-4" data-testid={`admin-note-${n.slug}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <a
                    href={`/notes/${n.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-display text-sm text-white hover:text-[#8aa8ff] inline-flex items-center gap-1.5 max-w-full"
                    data-testid={`admin-note-link-${n.slug}`}
                  >
                    {n.title} <ExternalLink size={11} />
                  </a>
                  {n.hidden_at && (
                    <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-300 font-mono-oak" data-testid={`admin-note-hidden-tag-${n.slug}`}>
                      hidden
                    </span>
                  )}
                </div>
                <div className="text-[11px] font-mono-oak text-white/40 mt-1 truncate">
                  {n.owner_name} · {n.snippet_count} clip{n.snippet_count === 1 ? "" : "s"} · {n.views} view{n.views === 1 ? "" : "s"} · {n.slug}
                </div>
                {n.preview && <div className="text-[12px] text-white/55 mt-2 line-clamp-2">{n.preview}</div>}
                {n.flag_reason && (
                  <div className="text-[11px] text-rose-300/80 mt-1">Reason: {n.flag_reason}</div>
                )}
              </div>
              <div className="shrink-0 flex flex-col gap-1.5">
                {n.hidden_at ? (
                  <button
                    onClick={() => unhide(n.slug)}
                    className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#00FF9D]/40 bg-[#00FF9D]/10 text-[#00FF9D] hover:bg-[#00FF9D]/20 transition"
                    data-testid={`admin-note-unhide-${n.slug}`}
                  >
                    <EyeIcon size={11} /> Unhide
                  </button>
                ) : (
                  <button
                    onClick={() => hide(n.slug)}
                    className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-rose-400/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20 transition"
                    data-testid={`admin-note-hide-${n.slug}`}
                  >
                    <EyeOff size={11} /> Hide
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


/**
 * Iter67 — Mini-game analytics card. Surfaces three things at a glance:
 *   1. Top-line summary (games played, passed, rewarded, replayed) — so
 *      we can monitor whether learners actually finish what we ask them
 *      to play.
 *   2. Per-mechanic breakdown — pass rate per `match / fill / order / tf`.
 *      A mechanic with a pass rate < 40% is probably too hard (or the
 *      LLM prompt is producing bad questions); > 90% is too easy.
 *   3. Top 5 paths by game engagement — useful to see which topics
 *      generators reliably produce engaging games for.
 *
 * Read-only for now; the goal is feedback, not configuration.
 */
function GamesAnalyticsSection({ games }) {
  const s = games?.summary || {};
  const mechanics = games?.by_mechanic || [];
  const topPaths = games?.top_paths || [];
  const passRate = s.played_14d > 0 ? Math.round((s.passed_14d / s.played_14d) * 100) : 0;
  const replayRate = s.played_14d > 0 ? Math.round((s.replays_14d / s.played_14d) * 100) : 0;

  return (
    <section className="glass p-6" data-testid="admin-games-analytics">
      <div className="flex items-center gap-2 label-eyebrow mb-4">
        <Gamepad2 size={12} className="text-[#9bc7ff]" /> Mini-game analytics · last 14 days
      </div>

      {/* Headline numbers */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="games-stat-played">
          <div className="text-[10px] uppercase tracking-wider text-white/45 font-mono-oak">Played</div>
          <div className="font-display text-2xl tabular-nums mt-1">{s.played_14d || 0}</div>
        </div>
        <div className="rounded-xl border border-[#00FF9D]/25 bg-[#00FF9D]/[0.04] p-4" data-testid="games-stat-passed">
          <div className="text-[10px] uppercase tracking-wider text-[#9bffcc] font-mono-oak">Pass rate</div>
          <div className="font-display text-2xl tabular-nums mt-1">{passRate}%</div>
          <div className="text-[10px] text-white/45 font-mono-oak">{s.passed_14d || 0} passed</div>
        </div>
        <div className="rounded-xl border border-[#FFD700]/25 bg-[#FFD700]/[0.04] p-4" data-testid="games-stat-rewarded">
          <div className="text-[10px] uppercase tracking-wider text-[#ffd86b] font-mono-oak">Credits granted</div>
          <div className="font-display text-2xl tabular-nums mt-1">{s.rewarded_14d || 0}</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="games-stat-replays">
          <div className="text-[10px] uppercase tracking-wider text-white/45 font-mono-oak">Replay rate</div>
          <div className="font-display text-2xl tabular-nums mt-1">{replayRate}%</div>
          <div className="text-[10px] text-white/45 font-mono-oak">{s.replays_14d || 0} replays</div>
        </div>
      </div>

      {/* Per-mechanic */}
      {mechanics.length > 0 && (
        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-wider text-white/45 font-mono-oak mb-2">
            By mechanic
          </div>
          <div className="divide-y divide-white/5">
            {mechanics.map((m) => {
              const pct = Math.round((m.pass_rate || 0) * 100);
              const accent =
                pct < 40 ? "#ff8a8a"
                  : pct > 90 ? "#ffd86b"
                  : "#00FF9D";
              return (
                <div
                  key={m.mechanic}
                  className="flex items-center gap-4 py-2.5"
                  data-testid={`games-mechanic-row-${m.mechanic}`}
                >
                  <div className="w-24 font-display text-sm capitalize text-white/90">{m.mechanic}</div>
                  <div className="flex-1 h-1.5 rounded-full bg-white/8 overflow-hidden">
                    <div
                      className="h-full transition-all"
                      style={{ width: `${pct}%`, background: accent }}
                    />
                  </div>
                  <div className="text-xs font-mono-oak text-white/60 tabular-nums w-28 text-right">
                    {pct}% pass · {m.played} played
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top paths by engagement */}
      {topPaths.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/45 font-mono-oak mb-2">
            Top paths by game engagement
          </div>
          <div className="divide-y divide-white/5">
            {topPaths.map((p, i) => (
              <div
                key={p.path_id}
                className="flex items-center gap-4 py-2.5"
                data-testid={`games-top-path-${i}`}
              >
                <div className="font-display text-lg text-white/40 w-6 text-right">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm truncate">{p.goal || "(no title)"}</div>
                  <div className="text-[11px] font-mono-oak text-white/35 truncate">{p.path_id}</div>
                </div>
                <div className="text-xs font-mono-oak text-white/65 tabular-nums whitespace-nowrap">
                  {p.played} played · {Math.round((p.pass_rate || 0) * 100)}% pass
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
